import {
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import {
  httpsCallable,
} from "firebase/functions";

import {
  auth,
  db,
  functions,
} from "../../firebase/config";

import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
} from "./authTypes";

const normalizeEmail = (email) =>
  typeof email === "string"
    ? email.trim().toLowerCase()
    : "";

/* =========================
   LOGIN
========================= */

export const login =
  (
    email,
    password,
    navigate,
    toast
  ) =>
  async (dispatch) => {
    dispatch({
      type: LOGIN_REQUEST,
    });

    sessionStorage.setItem(
      "loginValidationInProgress",
      "true"
    );

    try {
      const normalizedEmail =
        normalizeEmail(email);

      const result =
        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );

      const profileSnapshot =
        await getDoc(
          doc(
            db,
            "users",
            result.user.uid
          )
        );

      if (!profileSnapshot.exists()) {
        await signOut(auth);

        const message =
          "Your account setup is incomplete. Contact an administrator.";

        dispatch({
          type: LOGIN_FAIL,
          payload: message,
        });

        toast({
          title: "Account unavailable",
          description: message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });

        return;
      }

      const profile =
        profileSnapshot.data();

      /*
       * New accounts must verify their email before
       * administrator approval can be requested.
       */
      if (
        profile.emailVerificationRequired ===
          true &&
        result.user.emailVerified !== true
      ) {
        await signOut(auth);

        const message =
          "Verify your email address before continuing.";

        dispatch({
          type: LOGIN_FAIL,
          payload: message,
        });

        navigate("/login", {
          replace: true,
          state: {
            verificationRequired: true,
            verificationEmail:
              normalizedEmail,
          },
        });

        toast({
          title:
            "Email verification required",
          description:
            "Open the verification email we sent you, then sign in again.",
          status: "warning",
          duration: 7000,
          isClosable: true,
        });

        return;
      }

      /*
       * A verified pending account requests approval
       * before being signed back out.
       */
      if (
        profile.approvalStatus ===
        "pending"
      ) {
        toast({
  title:
    "Requesting administrator approval",
  description:
    "Please wait while your account request is submitted.",
  status: "info",
  duration: 5000,
  isClosable: true,
});
        let notificationFailed = false;

        try {
          const requestApproval =
            httpsCallable(
              functions,
              "requestAccountApproval"
            );

          await requestApproval();
        } catch (approvalError) {
          notificationFailed = true;

          console.error(
            "Approval request notification failed:",
            {
              code:
                approvalError.code ||
                "unknown",
            }
          );
        }

        await signOut(auth);

        const message =
          notificationFailed
            ? "Your email is verified and your account is pending approval, but the administrator notification could not be delivered. Please try signing in again later."
            : "Your email is verified and your account is awaiting administrator approval.";

        dispatch({
          type: LOGIN_FAIL,
          payload: message,
        });

        navigate("/login", {
          replace: true,
          state: {
            approvalPending: true,
            verificationEmail:
              normalizedEmail,
          },
        });

        toast({
          title: "Approval pending",
          description: message,
          status:
            notificationFailed
              ? "warning"
              : "info",
          duration: 8000,
          isClosable: true,
        });

        return;
      }

      if (
        profile.approvalStatus ===
        "denied"
      ) {
        await signOut(auth);

        const message =
          "This account request was not approved. Contact an administrator if you believe this is an error.";

        dispatch({
          type: LOGIN_FAIL,
          payload: message,
        });

        toast({
          title:
            "Account not approved",
          description: message,
          status: "error",
          duration: 7000,
          isClosable: true,
        });

        return;
      }

      /*
       * Covers manually deactivated accounts that are
       * not awaiting an approval decision.
       */
      if (profile.active === false) {
        await signOut(auth);

        const message =
          "This account has been deactivated. Contact an administrator.";

        dispatch({
          type: LOGIN_FAIL,
          payload: message,
        });

        toast({
          title: "Account deactivated",
          description: message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });

        return;
      }

      dispatch({
        type: LOGIN_SUCCESS,
        payload: {
          uid: result.user.uid,
          email: result.user.email,
          displayName:
            result.user.displayName ||
            profile.displayName ||
            profile.firstName ||
            "",
          ...profile,
        },
      });

      navigate("/dashboard");

      toast({
        title: "Login successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Login failed:", {
        code:
          error.code || "unknown",
      });

      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error(
            "Failed login cleanup failed:",
            {
              code:
                signOutError.code ||
                "unknown",
            }
          );
        }
      }

      const message =
        "Please check your email and password and try again.";

      dispatch({
        type: LOGIN_FAIL,
        payload: message,
      });

      toast({
        title: "Login failed",
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      sessionStorage.removeItem(
        "loginValidationInProgress"
      );
    }
  };

/* =========================
   RESEND VERIFICATION EMAIL
========================= */

export const resendVerificationEmail =
  (
    email,
    password,
    toast
  ) =>
  async (dispatch) => {
    const normalizedEmail =
      normalizeEmail(email);

    if (
      !normalizedEmail ||
      !password
    ) {
      toast({
        title:
          "Enter your email and password",
        description:
          "Both fields are required before requesting another verification email.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      return;
    }

    dispatch({
      type: LOGIN_REQUEST,
    });

    sessionStorage.setItem(
      "loginValidationInProgress",
      "true"
    );

    try {
      const result =
        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );

      if (result.user.emailVerified) {
        await signOut(auth);

        dispatch({
          type: LOGIN_FAIL,
          payload: null,
        });

        toast({
          title:
            "Email already verified",
          description:
            "Your email is already verified. Sign in to check your account approval status.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      await sendEmailVerification(
        result.user,
        {
          url:
            `${window.location.origin}` +
            "/login",
          handleCodeInApp: false,
        }
      );

      await signOut(auth);

      dispatch({
        type: LOGIN_FAIL,
        payload: null,
      });

      toast({
        title:
          "Verification email sent",
        description:
          "Check your inbox and spam folder, then open the verification link.",
        status: "success",
        duration: 7000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Verification email request failed:",
        {
          code:
            error.code || "unknown",
        }
      );

      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error(
            "Verification sign-out failed:",
            {
              code:
                signOutError.code ||
                "unknown",
            }
          );
        }
      }

      let message =
        "The verification email could not be sent. Check your email and password and try again.";

      if (
        error.code ===
        "auth/too-many-requests"
      ) {
        message =
          "Too many verification emails were requested. Wait a few minutes and try again.";
      }

      dispatch({
        type: LOGIN_FAIL,
        payload: message,
      });

      toast({
        title:
          "Verification email not sent",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      sessionStorage.removeItem(
        "loginValidationInProgress"
      );
    }
  };

/* =========================
   FORGOT PASSWORD
========================= */

export const forgotPassword =
  (email, toast) => async () => {
    const normalizedEmail =
      normalizeEmail(email);

    if (!normalizedEmail) {
      toast({
        title:
          "Enter your email address",
        description:
          "Enter your email above, then select Forgot Password again.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });

      return;
    }

    try {
      await sendPasswordResetEmail(
        auth,
        normalizedEmail
      );
    } catch (error) {
      /*
       * Do not reveal whether an email address has an
       * account. Always show the same response.
       */
      console.error(
        "Password reset request failed:",
        {
          code:
            error.code || "unknown",
        }
      );
    }

    toast({
      title: "Check your email",
      description:
        "If an account exists for that email, password-reset instructions will be sent.",
      status: "success",
      duration: 6000,
      isClosable: true,
    });
  };

/* =========================
   LOGOUT
========================= */

export const logout =
  (navigate, toast) =>
  async (dispatch) => {
    try {
      await signOut(auth);

      dispatch({
        type: LOGOUT,
      });

      navigate("/login");

      toast({
        title: "Logged out",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Logout failed:", {
        code:
          error.code || "unknown",
      });

      toast({
        title: "Unable to log out",
        description:
          "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };